import React from "react"
import PropTypes from "prop-types"
import { Link } from "react-router-dom"

import ProgressBarAnimate from "components/ProgressBarAnimate/ProgressBarAnimate"
import JSONLd from "components/JSONLd/JSONLd"
import Icon, { IconTypes } from "components/Icon/Icon"

import FormatterHelper from "helpers/formatter"
import dateHelper from "helpers/date"
import pictureHelper from "helpers/picture"

import routes from "consts/routes"
import i18n from "consts/i18n"

import styles from "./tileVideo.css"
import { LOCALE } from "consts/locale"

export default function TileVideo({ displayDate, displaySubtitle, isLive, isRecorded, isReplay, tile, useMicroData }) {
  const date = new Date(tile.getStartAt() * 1000)

  let progress = null

  if (isLive && tile.getVideo()) {
    const now = +new Date()
    if (tile.getStartAt() * 1000 <= now && now <= tile.getEndAt() * 1000) {
      progress = { start: tile.getStartAt(), end: tile.getEndAt() }
    }
  }

  let channelId = ""
  let channelTitle = ""

  if (tile.getChannel()) {
    channelId = tile.getChannel().getId()
    channelTitle = tile.getChannel().getTitle()
  }

  const programLink = FormatterHelper.basic(routes.program, {
    locale: LOCALE,
    programId: tile.getId(),
    channelId: channelId,
    programSlug: tile.getSlug(),
  })

  const tileIsUnavailable = !tile?.data?.is_available || tile?.getBroadcastDate() === i18n.not_broadcasted
  const title = tile?.data?.title
  const seasonAndEpisode = tile?.data?.subtitle?.slice(0, 6)
  const broadcastDate = tile?.getBroadcastDate()
  const typology = tile?.data?.metadata?.program_category
  const kind = tile?.data?.metadata?.program_kind
  const channel = tile?.data?.metadata?.channel_name
  const programTitle = tile?.data?.metadata?.program_title
  const episodeNumber = tile?.data?.metadata?.episode_number
  const seasonNumber = tile?.data?.metadata?.season_number

  const liveProgram = isLive || broadcastDate.includes("plus que")

  return (
    <article className={styles.item} itemScope itemType="https://schema.org/TVEpisode">
      {useMicroData && tile.getStartAt() && tile.getEndAt() && <JSONLd type="Event" data={tile.getEventMicroData()} />}
      <Link className={styles.linkTile} to={programLink} aria-label="link to program">
        <picture className={styles.poster}>
          <img
            loading="lazy"
            className={styles.posterImg}
            src={pictureHelper.replaceProtocol(tile.getImageUrl())}
            alt={`${tile.getChannel() ? channelTitle : tile.getTitle()} - ${tile.getTitle()}${isReplay ? " en replay" : ""}`}
            width={200}
            height={263}
          />
          {progress && <ProgressBarAnimate rootStyle={styles.progressBar} {...progress} />}
        </picture>
        <div className={styles.thumbnailMetaWrap}>
          {!tileIsUnavailable && !isReplay && <p className={styles.thumbnailBroadcastDate}>{liveProgram ? "En direct" : broadcastDate}</p>}
          {isLive || !displayDate || !displaySubtitle || isReplay ? null : (
            <p className={styles.thumbnailBroadcastDate}>
              {tile.isLive() ? (
                <span>
                  {dateHelper.getHoursString(date)}:{dateHelper.getMinutesString(date)}
                </span>
              ) : (
                tile.getRelativeDate()
              )}
            </p>
          )}
          <h3 className={styles.thumbnailTitle}>
            {title} {!!seasonAndEpisode && ` - ${seasonAndEpisode}`}
          </h3>
          {programTitle && <meta itemProp="partOfSeries" content={programTitle} />}
          {episodeNumber !== "0" && <meta itemProp="episodeNumber" content={episodeNumber} />}
          {episodeNumber !== "0" && seasonNumber && <meta itemProp="seasonNumber" content={seasonNumber} />}
          <ul className={styles.thumbnailMetaTypology}>
            <li>{typology}</li>
            <li>{kind}</li>
            <li>{channel}</li>
          </ul>
        </div>
      </Link>
      {isRecorded && <Icon type={IconTypes.flag} rootStyle={styles.flag} />}
    </article>
  )
}

TileVideo.propTypes = {
  displayDate: PropTypes.bool,
  displaySubtitle: PropTypes.bool,
  isLive: PropTypes.bool,
  isRecorded: PropTypes.bool,
  isReplay: PropTypes.bool,
  tile: PropTypes.object.isRequired,
  useMicroData: PropTypes.bool,
}

TileVideo.defaultProps = {
  displayDate: true,
  displaySubtitle: true,
  isLive: false,
  isRecorded: false,
  isReplay: false,
  useMicroData: false,
}
